import {
  decimal,
  mapAlternateValue,
  formatToCurrency,
  getComponentKey,
  formatDecimalsByValue
} from "src/utils/genericFunctions";
import stringToNumber from "src/utils/stringToNumber";
import { InventoryTableData, InventoryTableInitialState } from "../interface";
import {
  API_STATUS,
  API_STATUS_TYPE,
  DOC_STATUS,
  RECEIVING_TYPE_ID
} from "src/config/constants/genericConstants";

export const initialState: InventoryTableInitialState = {
  data: null,
  recordsCount: null,
  status: "idle",
  error: null,
  totalCasesReceived: 0,
  totalUnitsReceived: 0
};

export const getFormattedPackAndSize = (packOrSize: string) => {
  const [pack = "", sizeStr = ""] = packOrSize?.split("/") ?? ["", ""];
  let size: string | number = stringToNumber(sizeStr);
  if (size === 0) {
    size = "-";
  } else {
    size = size.toFixed(2);
  }
  return { pack: pack || "-", size };
};

const isDsdOrWt = (type: string) =>
  type.toUpperCase() === RECEIVING_TYPE_ID.DSD ||
  type.toUpperCase() === RECEIVING_TYPE_ID.TRANSFER;

const isDsdOrWtOrCredit = (type: string) =>
  isDsdOrWt(type) || type?.toUpperCase() === RECEIVING_TYPE_ID.CREDIT;

export const formatInventoryTableData = (
  inventoryData: InventoryTableData[],
  isRandomWeightItem: boolean
) => {
  let totalCasesReceived: number | string = 0;
  let totalUnitsReceived: number | string = 0;
  const getFormattedQuantity = (
    item: InventoryTableData,
    pack: string,
    size: string
  ) => {
    const quantity = item?.quantity;
    let formattedQuantity: number | string = "-";
    const calculateTotalUnitCaseReceived = () => {
      totalCasesReceived =
        Number(totalCasesReceived) + Number(decimal(quantity));
      totalUnitsReceived =
        Number(totalUnitsReceived) +
        Number(decimal(quantity)) * Number(isRandomWeightItem ? size : pack);
    };
    if (isDsdOrWtOrCredit(item?.type)) {
      isDsdOrWt(item?.type) &&
        item?.status === DOC_STATUS.RECEIVED &&
        calculateTotalUnitCaseReceived();
      formattedQuantity = decimal(quantity);
    }
    return formattedQuantity;
  };

  const getFormattedQuantityEaches = (
    item: InventoryTableData,
    pack: string
  ) => {
    const quantity = item?.quantity?.toString();
    const type = item?.type?.toUpperCase();

    // For no qty value or if type is corrective billing
    // or if item is random weight, return "-"
    if (
      !Number(quantity) ||
      isRandomWeightItem ||
      type === RECEIVING_TYPE_ID.CORRECTIVE_BILLING
    ) {
      return "-";
    }

    // For Transfer, DSD, and Credit, calculate quantity eaches by multiplying by pack
    if (isDsdOrWtOrCredit(type)) {
      const quantityEaches = Number(quantity) * Number(pack);

      return decimal(quantityEaches.toString());
    }

    // For other types, the quantity will be already in eaches
    return decimal(quantity);
  };

  const getFormattedPackSizeQuantity = (item: InventoryTableData) => {
    const { pack, size } = getFormattedPackAndSize(item?.packOrSize);
    return {
      packOrSize: `${pack}/${size}`,
      quantity: getFormattedQuantity(item, pack, size),
      quantityEaches: getFormattedQuantityEaches(item, pack)
    };
  };
  const formattedInventoryData = (inventoryItem: InventoryTableData) => {
    const { packOrSize, quantity, quantityEaches } =
      getFormattedPackSizeQuantity(inventoryItem);
    return {
      ...inventoryItem,
      packOrSize,
      quantity,
      quantityEaches,
      transfer: formatToCurrency(stringToNumber(inventoryItem.transfer)),
      retail: formatToCurrency(stringToNumber(inventoryItem.retail)),
      value: formatToCurrency(stringToNumber(inventoryItem.value)),
      type: mapAlternateValue(inventoryItem?.type),
      unitRetail: inventoryItem.unitRetail
        ? formatToCurrency(stringToNumber(inventoryItem.unitRetail))
        : "",
      margin: inventoryItem.margin ? inventoryItem.margin + "%" : "-",
      key: getComponentKey()
    };
  };

  return {
    formattedInventoryData: inventoryData.map(formattedInventoryData),
    totalCasesReceived: formatDecimalsByValue(totalCasesReceived),
    totalUnitsReceived: formatDecimalsByValue(totalUnitsReceived)
  };
};

export const handleFullFillForFetchInventoryTableData = (
  state: InventoryTableInitialState,
  action: {
    payload: { data: any[] };
    meta: { arg: { isRandomWeightItem: boolean } };
  }
) => {
  state.status = API_STATUS.IDLE as API_STATUS_TYPE;
  const { data } = action.payload;
  if (data?.length > 0) {
    const { formattedInventoryData, totalCasesReceived, totalUnitsReceived } =
      formatInventoryTableData(data, action?.meta?.arg?.isRandomWeightItem);
    state.data = formattedInventoryData as InventoryTableData[];
    state.totalCasesReceived = totalCasesReceived;
    state.totalUnitsReceived = totalUnitsReceived;
    state.recordsCount = formattedInventoryData.length;
  } else {
    state.data = null;
    state.totalCasesReceived = "0";
    state.totalUnitsReceived = "0";
    state.recordsCount = 0;
  }
};
